<template>
  <div>
    <div data-cy="bank-connections-section">
      <div :class="$style.container">
        <img
          v-if="showPlaidPreview"
          :class="$style['plaid-img']"
          :src="plaidPreview"
          :alt="$t('components.containerConnectBankAccounts.plaidModalPreview')"
        />
        <div v-else :class="$style.spacer"></div>
      </div>
      <BankOverlayLoadingIcon v-if="loading" />
    </div>
    <BankIntegrations
      ref="bankIntegrations"
      @success="handlePlaidSuccess"
      @error="handleErrors"
      @exit="handleBankingConnectExit"
      @plaidOpened="trackPlaidModal(SEGMENT_EVENTS.fe_plaid_opened)"
      @flowEvent="trackPlaidFlowEvent"
    />
  </div>
</template>

<script>
import { useStore } from 'vuex';
import { mapRequestStatuses } from '@/utils/vuex-api-utils';
import analytics, {
  SEGMENT_EVENTS,
  trackBusinessProfileStepCompletion,
} from '@/utils/analytics';
import { INACTIVE_ROUTES } from '@/data/lexisnexis-types';
import { useProfiling } from '@/composables/useProfiling';
import plaidPreview from '@/assets/images/plaid-preview.png';

import BankOverlayLoadingIcon from '@/pages/banking/components/BankOverlayLoadingIcon.vue';
import BankIntegrations from './BankIntegrations.vue';

export default {
  components: {
    BankIntegrations,
    BankOverlayLoadingIcon,
  },
  props: {
    eventTrackingFields: { type: Object, default: () => {} },
    // eslint-disable-next-line vue/no-unused-properties
    showError: { type: Boolean, required: true },
  },
  setup() {
    const store = useStore();
    const profiling = useProfiling(INACTIVE_ROUTES.PLAID, {
      type: store.getters.eventTypeBasedOnLoginsCount,
    });

    return { profiling };
  },
  data() {
    return {
      loading: false,
      showPlaidPreview: true,
      SEGMENT_EVENTS,
    };
  },
  computed: {
    ...mapRequestStatuses({
      addBankConnectionRequest: 'ADD_USER_BANK_CONNECTION',
    }),
    plaidPreview: () => plaidPreview,
  },
  methods: {
    /** @public */
    openBankingIntegration(routingNumber) {
      this.loading = true;
      this.$emit('update:showError', false);
      this.$refs.bankIntegrations.openBankIntegration(
        this.currentRoute,
        routingNumber,
      );
    },
    async handlePlaidSuccess() {
      await this.$store.dispatchApiAction('FETCH_USER_BANK_ACCOUNTS');
      this.clearBankingConnectErrors();
      this.profiling.finish();
      trackBusinessProfileStepCompletion(
        this.$store.getters.businessProfileSteps,
      );
      this.$emit('plaidSuccess');
    },
    handleErrors(plaidMetadata) {
      if (!this.addBankConnectionRequest.isSuccess) {
        this.showPlaidPreview = false;
        if (plaidMetadata.error_code) {
          this.$store.commit(
            'CLEAR_REQUEST_STATUS',
            'ADD_USER_BANK_CONNECTION',
          );
        }
      }
      this.loading = false;
      this.$emit('plaidError', plaidMetadata);
    },
    clearBankingConnectErrors() {
      // Plaid will still emit error while user is retrying. This will clear the error state if the retry within the modal is successful
      this.loading = false;
      this.showPlaidPreview = true;
      this.$emit('update:showError', false);
    },
    handleBankingConnectExit() {
      this.loading = false;
      this.trackPlaidModal(SEGMENT_EVENTS.fe_plaid_quit);
      this.$emit('exit');
    },
    trackPlaidModal(action, relink) {
      analytics.track(action, {
        ...this.eventTrackingFields,
        relink: !!relink,
      });
    },
    trackPlaidFlowEvent({ eventName, metadata }, relink) {
      // sometimes if the user exits no error gets emitted by plaid but it is captured in the metadata in a flow event
      if (metadata.error_code) {
        this.handleErrors(metadata);
      }
      const bankTrackingProps = {
        ...this.eventTrackingFields,
        bank: metadata.institution_name,
        institution_id: metadata.institution_id,
        relink: !!relink,
      };

      if (eventName === 'SELECT_INSTITUTION') {
        analytics.track(SEGMENT_EVENTS.fe_select_bank, {
          ...bankTrackingProps,
        });
      } else if (eventName === 'SUBMIT_CREDENTIALS') {
        analytics.track(SEGMENT_EVENTS.fe_bank_account_attempt, {
          ...bankTrackingProps,
          link_session_id: metadata.link_session_id,
        });
      }
    },
  },
};
</script>

<style>
.p-message-wrapper {
  text-align: left;
}

.p-message p {
  font-weight: 400 !important;
}

.p-message .p-message-close.p-link {
  transform: translate(0, 0) !important;
  margin: -0.8rem -1.2rem 0 auto !important;
}

.p-message .p-message-close {
  min-width: 1.75rem !important;
  min-height: 1.75rem !important;
}
</style>

<style lang="less" module>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.plaid-img {
  width: 100%;
  max-width: 260px;
  padding-top: 10px;
}
.spacer {
  height: 64px;
}
</style>
